import PropTypes from "prop-types";
import React from "react";
import { Badge, Card, Col, Container, Row } from "react-bootstrap";

export const PuppyOverview = ({ puppy }) => {
  // Create professional story from available data
  const generatePuppyStory = (puppy) => {
    const name = puppy.cardHeaderTitle;
    const breed = puppy.breedLong || puppy.breed;
    const personality = puppy.personality;

    if (personality) {
      return `${name} is a professionally trained ${breed} who has completed our comprehensive development program. ${personality.replace(/😊|🐕|❤️|💖|🏠|🎾|🐾/g, "").trim()} Our expert trainers have worked closely with ${name} to ensure they are ready to seamlessly integrate into their new family.`;
    }

    return `${name} is a carefully selected ${breed} who has graduated from our exclusive professional training program. This exceptional companion combines natural intelligence, stable temperament, and advanced training to create the perfect family addition. ${name} has been evaluated and trained by certified professionals to ensure the highest standards of behavior and socialization.`;
  };

  const getHealthInfo = () => {
    return [
      {
        type: "Veterinary Health Certificate",
        status: "Current & Complete",
        issuer: "Licensed Veterinarian",
        icon: "🏥",
      },
      { type: "Vaccinations", status: "Up to date", next: "Due at 1 year" },
      { type: "Genetic Testing", status: "Clear", lab: "Embark Veterinary" },
      { type: "Microchip", status: "Registered", id: "Provided at delivery" },
    ];
  };

  const getBreederInfo = (puppy) => {
    return {
      name: puppy.breeder || "Certified Professional Breeder Network",
      experience: "15+ years breeding excellence",
      certifications: [
        "AKC Registered",
        "OFA Health Testing",
        "Professional Breeding Standards",
      ],
      location: "Verified breeding facility",
      reputation: "Exceptional bloodlines and health standards",
    };
  };

  const getTrainerCredentials = () => {
    return {
      name: "Professional Training Team",
      credentials: [
        "CCPDT Certified",
        "Professional Dog Trainer",
        "Behavior Specialist",
      ],
      experience: "10+ years training experience",
      specialties: [
        "Puppy Development",
        "Family Integration",
        "Advanced Obedience",
      ],
      philosophy: "Positive reinforcement methods with proven results",
    };
  };

  const healthInfo = getHealthInfo();
  const breederInfo = getBreederInfo(puppy);
  const trainerInfo = getTrainerCredentials();

  return (
    <div className="puppy-overview">
      <Container>
        {/* Professional Introduction */}
        <Row className="mb-5">
          <Col lg={8} className="mx-auto">
            <Card className="story-card">
              <Card.Body className="p-4">
                <h2 className="section-title text-center mb-4">
                  Meet {puppy.cardHeaderTitle}
                </h2>
                <div className="puppy-story">
                  <p className="lead text-muted">{generatePuppyStory(puppy)}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Key Information Cards */}
        <Row className="g-4 mb-5">
          {/* Breeder Information */}
          <Col md={6}>
            <Card className="info-card h-100">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">Breeder Excellence</h5>
              </Card.Header>
              <Card.Body>
                <div className="breeder-info">
                  <h6 className="text-primary">{breederInfo.name}</h6>
                  <div className="credential-item">
                    <strong>Experience:</strong> {breederInfo.experience}
                  </div>
                  <div className="credential-item">
                    <strong>Certifications:</strong>
                    <div className="mt-2">
                      {breederInfo.certifications.map((cert, index) => (
                        <Badge
                          key={index}
                          bg="light"
                          text="dark"
                          className="me-2 mb-1"
                        >
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="credential-item">
                    <strong>Standards:</strong> {breederInfo.reputation}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Trainer Information */}
          <Col md={6}>
            <Card className="info-card h-100">
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">Professional Training</h5>
              </Card.Header>
              <Card.Body>
                <div className="trainer-info">
                  <h6 className="text-success">{trainerInfo.name}</h6>
                  <div className="credential-item">
                    <strong>Experience:</strong> {trainerInfo.experience}
                  </div>
                  <div className="credential-item">
                    <strong>Certifications:</strong>
                    <div className="mt-2">
                      {trainerInfo.credentials.map((cert, index) => (
                        <Badge
                          key={index}
                          bg="light"
                          text="dark"
                          className="me-2 mb-1"
                        >
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="credential-item">
                    <strong>Specialties:</strong>{" "}
                    {trainerInfo.specialties.join(", ")}
                  </div>
                  <div className="credential-item">
                    <strong>Methods:</strong> {trainerInfo.philosophy}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Health & Certification */}
        <Row>
          <Col>
            <Card className="health-card">
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">Health Certification & Guarantees</h5>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  {healthInfo.map((item, index) => (
                    <Col md={6} lg={3} key={index}>
                      <div className="health-item">
                        <div className="health-header">
                          <h6 className="health-type">{item.type}</h6>
                        </div>
                        <div className="health-status">
                          <Badge bg="success" className="mb-2">
                            {item.status}
                          </Badge>
                        </div>
                        {item.issuer && (
                          <div className="health-detail">
                            <small className="text-muted">
                              <strong>Provider:</strong> {item.issuer}
                            </small>
                          </div>
                        )}
                        {item.lab && (
                          <div className="health-detail">
                            <small className="text-muted">
                              <strong>Laboratory:</strong> {item.lab}
                            </small>
                          </div>
                        )}
                        {item.next && (
                          <div className="health-detail">
                            <small className="text-muted">
                              <strong>Next:</strong> {item.next}
                            </small>
                          </div>
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Guarantees & Support */}
        <Row className="mt-4">
          <Col>
            <Card className="guarantee-card">
              <Card.Body className="text-center">
                <h5 className="text-primary mb-3">
                  Our Professional Guarantees
                </h5>
                <Row className="g-3">
                  <Col md={4}>
                    <div className="guarantee-item">
                      <h6>Health Guarantee</h6>
                      <p className="small text-muted">
                        Comprehensive health coverage and genetic guarantee
                      </p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="guarantee-item">
                      <h6>Training Guarantee</h6>
                      <p className="small text-muted">
                        Professional training standards with ongoing support
                      </p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="guarantee-item">
                      <h6>Lifetime Support</h6>
                      <p className="small text-muted">
                        Continued guidance and support throughout your
                        dog&apos;s life
                      </p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

PuppyOverview.propTypes = {
  puppy: PropTypes.object.isRequired,
};
